import { useEffect, useState } from 'react';
import { FormSection } from '../ui/FormSection';
import {
  REPORT_PROMPT_KINDS,
  REPORT_PROMPT_KIND_LABEL,
  fetchReportPrompts,
  saveReportPrompt,
  type ReportPromptKind,
  type ReportPrompts,
} from '../../api/reportApi';

// Owner-only "Custom prompts" editor (AI Settings, per channel): each of
// the four AI report system prompts (standard report, Multi-Channel
// Report, "Ask questions" answers, "Ask questions" suggestions) can be
// fully replaced for this channel - see backend/internal/report/
// prompts.go's header comment for the three-layer precedence (this
// channel's override > an operator-set ZMAP default > the compiled-in
// one, shown here as a placeholder when the field is empty) and the
// {{token}} substitution every prompt is rendered through.
export function CustomPromptsSection({ nickname }: { nickname: string }) {
  const [prompts, setPrompts] = useState<ReportPrompts | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPrompts(null);
    fetchReportPrompts(nickname)
      .then(setPrompts)
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load prompts.'));
  }, [nickname]);

  return (
    <FormSection title="Custom prompts" collapsible id="report-custom-prompts" defaultOpen={false}>
      <p className="text-xs text-black/50 dark:text-white/50 m-0">
        Each box below starts pre-filled with the instructions the AI currently follows for this channel - edit it
        directly and save, or reset to go back to the default.
      </p>
      {error && <p className="text-xs text-red-500 m-0">{error}</p>}
      {!prompts && !error && <p className="text-xs text-black/40 dark:text-white/40 m-0">Loading…</p>}
      {prompts && (
        <>
          {REPORT_PROMPT_KINDS.map((kind) => (
            <PromptField key={kind} nickname={nickname} kind={kind} prompts={prompts} onSaved={(v) => setPrompts((p) => (p ? { ...p, channelOverrides: { ...p.channelOverrides, [kind]: v || undefined } } : p))} />
          ))}
          <details className="text-xs text-black/50 dark:text-white/50">
            <summary className="cursor-pointer select-none">Available tokens</summary>
            <ul className="list-disc pl-4 pt-1 flex flex-col gap-0.5">
              {prompts.tokens.map((t) => (
                <li key={t.name}>
                  <code className="font-mono">{`{{${t.name}}}`}</code> — {t.description}
                </li>
              ))}
            </ul>
          </details>
        </>
      )}
    </FormSection>
  );
}

function PromptField({
  nickname,
  kind,
  prompts,
  onSaved,
}: {
  nickname: string;
  kind: ReportPromptKind;
  prompts: ReportPrompts;
  onSaved: (template: string) => void;
}) {
  const saved = prompts.channelOverrides[kind] ?? '';
  const effectiveDefault = prompts.zmapDefaults[kind] ?? prompts.defaults[kind];
  // The box is pre-filled with whatever's actually in effect right now
  // (the channel's own override if set, else the ZMAP/compiled default) -
  // not an empty field with a greyed-out hint - so editing starts from
  // the real, currently-active text.
  const effectiveValue = saved || effectiveDefault;
  const [draft, setDraft] = useState(effectiveValue);
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const dirty = draft !== effectiveValue;
  const usingOperatorDefault = !prompts.channelOverrides[kind] && !!prompts.zmapDefaults[kind];

  useEffect(() => setDraft(effectiveValue), [effectiveValue]);

  const save = async (template: string) => {
    setSaving(true);
    setFieldError(null);
    try {
      const result = await saveReportPrompt(nickname, kind, template);
      onSaved(result);
    } catch (err) {
      setFieldError(err instanceof Error ? err.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium text-black/50 dark:text-white/50">
        {REPORT_PROMPT_KIND_LABEL[kind]}
        {usingOperatorDefault && <span className="text-black/35 dark:text-white/35"> · using operator default</span>}
      </span>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={6}
        className="rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-3 py-2 text-xs font-mono outline-none focus:border-blue-500 resize-y"
      />
      <div className="flex items-center gap-2 justify-end">
        {fieldError && <span className="text-xs text-red-500 mr-auto">{fieldError}</span>}
        {saved && (
          <button
            type="button"
            onClick={() => save('')}
            disabled={saving}
            className="text-[11px] text-black/50 dark:text-white/50 hover:text-red-500 disabled:opacity-50"
          >
            Reset to default
          </button>
        )}
        <button
          type="button"
          onClick={() => save(draft)}
          disabled={saving || !dirty}
          className="rounded-md px-2.5 py-1 bg-blue-600 text-white text-[11px] font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </label>
  );
}
